import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { Stage, Layer } from 'react-konva'
import { loadSofaElement, type SofaElementProps } from './DynamicLoadSofaShape'

interface SofaBlueprintProps {
  id: number
  containerHeight?: string
  containerWidth?: string
  dimensions: {
    width?: number | null
    length?: number | null
    armrest_width?: number | null
    backrest_width?: number | null
    mattress_width?: number | null
    mattress_length?: number | null
    corner_part_length?: number | null
    angle?: number | null
    seat_height?: number | null
    extendable_part_length?: number | null
    number_of_big_pillows?: number | null
    number_of_small_pillows?: number | null
    spread_of_big_pillows?: number | null
    spread_of_small_pillows?: number | null
    size_of_big_pillow?: number | null
    size_of_pillow?: number | null
    corner_radius?: number | null
    seat_sections?: number | null
    backrest_sections?: number | null
    extension_type?: string | null
    backrest_type?: string | null
    covered_side?: boolean | null
    // Add any other properties that might be needed
    // For example, if you have a composition property:
    composition?: Array<{
      shape: string
      width: number
      height: number
      corner_part_length?: number
    }> | null
  }
  type: string
  showMetrics?: boolean
}

export default function SofaBlueprint({
  id,
  dimensions,
  type,
  containerHeight,
  containerWidth = '100%',
  showMetrics = false,
}: SofaBlueprintProps) {
  const [stageSize, setStageSize] = useState({ width: 400, height: 600 })
  const [SofaElement, setSofaElement] =
    useState<React.ComponentType<SofaElementProps> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Calculate dynamic container height based on shape height + 60px for labels
  const shapeHeight = dimensions?.length || 100
  const dynamicContainerHeight = containerHeight || `${shapeHeight + 150}px`

  const updateDimensions = useCallback(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect()
      setStageSize({ width, height })
    }
  }, [])

  useEffect(() => {
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [updateDimensions])

  useEffect(() => {
    const loadShape = async () => {
      try {
        const moduleExports = await loadSofaElement(type)
        if (moduleExports?.default) {
          setSofaElement(() => moduleExports.default)
        }
      } catch (error) {
        console.error('Error loading sofa element:', error)
      }
    }
    loadShape()
  }, [type])

  // Memoize calculated values to optimize re-renders
  const calculatedValues = useMemo(() => {
    const shapeWidth = dimensions?.width || 200

    const adjustedWidth = stageSize.width
    const adjustedHeight = stageSize.height // Adjust height to leave space for labels

    // Calculate scale only if shape is larger than container
    const needsScaling =
      shapeWidth > adjustedWidth || shapeHeight > adjustedHeight
    const scale = needsScaling
      ? Math.min(adjustedWidth / shapeWidth, adjustedHeight / shapeHeight)
      : 1

    return { shapeWidth, adjustedWidth, adjustedHeight, scale: scale * 0.9 }
  }, [dimensions?.width, shapeHeight, stageSize.width, stageSize.height])

  const { shapeWidth, adjustedWidth, adjustedHeight, scale } = calculatedValues

  // Force re-render when dimensions change
  useEffect(() => {
    // This effect ensures the component updates when dimensions change
    // The dependency array will trigger re-renders for any dimension changes
  }, [dimensions])

  return (
    <div
      ref={containerRef}
      className=" p-0 bg-white"
      style={{
        height: dynamicContainerHeight,
        width: containerWidth,
        position: 'relative',
      }}
    >
      <Stage
        width={stageSize.width}
        height={stageSize.height * 2}
        style={{ position: 'absolute', left: 0, top: 0 }}
        scale={{ x: scale, y: scale }}
      >
        <Layer>
          {SofaElement &&
            (() => {
              // Center calculation
              const shapeCenterX = shapeWidth / 2
              const shapeCenterY = shapeHeight / 2 + 0
              const stageCenterX = adjustedWidth / 2
              const stageCenterY = adjustedHeight / 2
              const x = stageCenterX / scale - shapeCenterX
              const y = stageCenterY / scale - shapeCenterY

              return (
                <SofaElement
                  id={id.toString()}
                  width={shapeWidth}
                  height={shapeHeight}
                  x={x}
                  y={y}
                  scale={scale}
                  stageWidth={stageSize.width}
                  stageHeight={stageSize.height}
                  armrestWidth={dimensions?.armrest_width ?? undefined}
                  backrestWidth={dimensions?.backrest_width ?? undefined}
                  mattressWidth={dimensions?.mattress_width ?? undefined}
                  mattressLength={dimensions?.mattress_length ?? undefined}
                  cornerPartLength={dimensions?.corner_part_length ?? undefined}
                  currentRotation={0}
                  rotation={0}
                  angle={dimensions?.angle ?? undefined}
                  verticalMetric={showMetrics}
                  horizontalMetric={showMetrics}
                  composition={dimensions?.composition ?? undefined}
                  cornerRadius={dimensions?.corner_radius ?? undefined}
                  extendablePartLength={dimensions.extendable_part_length}
                  numberOfBigPillows={dimensions.number_of_big_pillows}
                  numberOfSmallPillows={dimensions.number_of_small_pillows}
                  spreadOfBigPillows={dimensions.spread_of_big_pillows}
                  spreadOfSmallPillows={dimensions.spread_of_small_pillows}
                  sizeOfBigPillow={dimensions.size_of_big_pillow}
                  sizeOfPillow={dimensions.size_of_pillow}
                  seatSections={dimensions.seat_sections}
                  backrestSections={dimensions.backrest_sections}
                  extensionType={dimensions.extension_type}
                  backrestType={dimensions.backrest_type}
                  coveredSide={dimensions.covered_side}
                />
              )
            })()}
        </Layer>
      </Stage>
    </div>
  )
}
